/**
 * BuilderIframe — CRM component that embeds an external builder via iframe.
 * 
 * Uses the @tagadapay/builder-protocol to communicate with the builder.
 * 
 * Separation of concerns:
 * - The builder (Studio) handles visual editing + build + deploy via public API
 * - The CRM (host) handles orchestration: auth, stepConfig, scripts, pixels, A/B, routing
 * - The builder deploys ITSELF, then notifies the host of the result IDs
 * - The host updates the step config to point to the new deployment/instance
 * 
 * This is the first "node editor" type — for visual page editing (Studio).
 * The same pattern will be reused for email builders, form editors, etc.
 */

import { useCallback, useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react'
import { BuilderHost } from '@tagadapay/builder-protocol/host'
import type { 
  SaveMessage,
  RequestPreviewMessage,
  RequestResourcesMessage,
  PreviewSessionMessage,
  ResourcesMessage,
  RequestMediaLibraryMessage,
  MediaLibraryMessage,
  RegisterMediaAssetMessage,
  MediaAssetRegisteredMessage,
  StepType,
  HostCapability,
} from '@tagadapay/builder-protocol'
import { Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import toast from 'react-hot-toast'

interface BuilderIframeProps {
  /** URL of the builder to embed (e.g. Studio URL) */
  builderUrl: string
  /** Store ID */
  storeId: string
  /** Funnel ID */
  funnelId?: string
  /** Step ID being edited */
  stepId?: string
  /** Step type */
  stepType?: StepType
  /** Session token for the builder to authenticate public API calls (empty while loading) */
  sessionToken: string
  /** Whether session authentication failed */
  authError?: boolean
  /** Retry authentication callback */
  onRetryAuth?: () => void
  /** Plugin to load for editing (null for create mode) */
  plugin?: {
    pluginId: string
    deploymentId: string
    pageId?: string
    sourceData?: unknown
  } | null
  /** Edit mode */
  mode?: 'edit' | 'new-page' | 'create'
  /** Suggested name for new pages (helps generate unique pluginId) */
  suggestedName?: string
  /** Called when builder signals unsaved changes */
  onDirtyChange?: (isDirty: boolean) => void
  /** Called when builder has deployed and sends result IDs */
  onSave?: (payload: SaveMessage['payload']) => Promise<void>
  /** Called when builder wants to close (back to step settings) */
  onClose?: () => void
  /** Creates a real preview session via CRM backend (returns preview URL + tokens) */
  onRequestPreview?: (payload: RequestPreviewMessage['payload']) => Promise<PreviewSessionMessage['payload']>
  /** Fetches store resources (products, offers) for the builder */
  onRequestResources?: (payload: RequestResourcesMessage['payload']) => Promise<ResourcesMessage['payload']>
  /** Fetches media library listing for the builder */
  onRequestMediaLibrary?: (payload: RequestMediaLibraryMessage['payload']) => Promise<MediaLibraryMessage['payload']>
  /** Registers a newly uploaded media asset from the builder */
  onRegisterMediaAsset?: (payload: RegisterMediaAssetMessage['payload']) => Promise<MediaAssetRegisteredMessage['payload']>
  /** Called when builder requests navigation to a different funnel step */
  onNavigateStep?: (stepId: string) => void
  /** Step config to forward to the builder on init and on change */
  stepConfig?: Record<string, unknown> | null
  /** Funnel topology (steps + edges) for the builder's step navigator */
  funnelContext?: {
    funnelName: string
    steps: Array<{ id: string; name: string; type: string; isDefault?: boolean; path?: string }>
    edges: Array<{ source: string; target: string }>
  } | null
  /** Additional class names */
  className?: string
  /** Whether to allow fullscreen toggle */
  allowFullscreen?: boolean
}

type ConnectionState = 'connecting' | 'ready' | 'error'

export interface BuilderIframeHandle {
  requestSave: () => void
  /**
   * Soft-navigate to a different step without destroying/recreating the iframe.
   * Sends init + load to the running Studio instance so the transition is seamless.
   */
  softNavigate: (opts: {
    stepId: string
    stepType?: StepType
    sessionToken: string
    plugin: { pluginId: string; deploymentId: string; pageId?: string; sourceData?: unknown } | null
    mode?: 'edit' | 'create'
    stepConfig?: Record<string, unknown> | null
  }) => void
}

export const BuilderIframe = forwardRef<BuilderIframeHandle, BuilderIframeProps>(function BuilderIframe({
  builderUrl,
  storeId,
  funnelId,
  stepId,
  stepType,
  sessionToken,
  authError,
  onRetryAuth,
  plugin,
  mode = 'edit',
  suggestedName,
  onDirtyChange,
  onSave,
  onClose,
  onRequestPreview,
  onRequestResources,
  onRequestMediaLibrary,
  onRegisterMediaAsset,
  onNavigateStep,
  stepConfig,
  funnelContext,
  className = '',
  allowFullscreen = true,
}, ref) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const hostRef = useRef<BuilderHost | null>(null)
  const connectionStateRef = useRef<ConnectionState>('connecting')
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Tracks the sessionToken that was already sent during the initial ready handshake,
  // so the late-token effect doesn't re-send it redundantly.
  const sentTokenRef = useRef<string | null>(null)
  // FE-12: fallback timer for the deferred cold-open load (ready fired before
  // the session token was minted; see the ready handler).
  const pendingLoadTimerRef = useRef<number | null>(null)

  // Callback refs to avoid stale closures in the host event handlers
  const onSaveRef = useRef(onSave)
  const onDirtyChangeRef = useRef(onDirtyChange)
  const onCloseRef = useRef(onClose)
  const onRequestPreviewRef = useRef(onRequestPreview)
  const onRequestResourcesRef = useRef(onRequestResources)
  const onRequestMediaLibraryRef = useRef(onRequestMediaLibrary)
  const onRegisterMediaAssetRef = useRef(onRegisterMediaAsset)
  const onNavigateStepRef = useRef(onNavigateStep)
  const propsRef = useRef({ storeId, funnelId, stepId, stepType, sessionToken, plugin, mode, suggestedName, stepConfig, funnelContext })

  useEffect(() => {
    onSaveRef.current = onSave
    onDirtyChangeRef.current = onDirtyChange
    onCloseRef.current = onClose
    onRequestPreviewRef.current = onRequestPreview
    onRequestResourcesRef.current = onRequestResources
    onRequestMediaLibraryRef.current = onRequestMediaLibrary
    onRegisterMediaAssetRef.current = onRegisterMediaAsset
    onNavigateStepRef.current = onNavigateStep
    propsRef.current = { storeId, funnelId, stepId, stepType, sessionToken, plugin, mode, suggestedName, stepConfig, funnelContext }
  })

  // Keep ref in sync with state (ref is readable inside closures without stale capture)
  const updateConnectionState = (state: ConnectionState) => {
    connectionStateRef.current = state
    setConnectionState(state)
  }

  // Stable identity for the plugin being edited — changes when switching designs
  const identityOf = (p?: { pluginId: string; deploymentId: string; pageId?: string } | null, m?: string) =>
    p ? `${p.pluginId}:${p.deploymentId}:${p.pageId || ''}` : (m || 'edit')
  const pluginIdentity = identityOf(plugin, mode)
  // Which design identity the running Studio last received via load(). Guards
  // the prop-driven in-place switch below against double-loading when
  // softNavigate (which runs before React re-renders with the new props)
  // already sent it (audit FE-05).
  const lastLoadedIdentityRef = useRef<string | null>(null)

  /** Initialize the host and set up event handlers */
  useEffect(() => {
    updateConnectionState('connecting')

    const host = new BuilderHost()
    hostRef.current = host

    // ─── Handle: Builder Ready ─────────────────────────────────────
    host.on('ready', (payload) => {
      console.log('[BuilderIframe] Builder ready:', payload)
      updateConnectionState('ready')

      const p = propsRef.current
      const capabilities: HostCapability[] = ['preview', 'resources']
      sentTokenRef.current = p.sessionToken || null

      host.init({
        storeId: p.storeId,
        funnelId: p.funnelId,
        stepId: p.stepId,
        stepType: p.stepType,
        sessionToken: p.sessionToken,
        capabilities,
        stepConfig: p.stepConfig,
        ui: {
          theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
        },
      })

      // FE-12: on a cold open, `ready` usually fires BEFORE the session token
      // has been minted. Loading here meant a tokenless load followed by a
      // full init+load re-send when the token arrived — a warm Studio
      // processed two loads. Defer the load until the token exists (the
      // late-token effect below sends init+load exactly once); a fallback
      // timer preserves the old tokenless-load behavior in case the token
      // never arrives (e.g. mint failure) so the editor can't hang forever.
      if (p.sessionToken) {
        lastLoadedIdentityRef.current = identityOf(p.plugin, p.mode)
        if (p.plugin) {
          host.load({
            pluginId: p.plugin.pluginId,
            deploymentId: p.plugin.deploymentId,
            pageId: p.plugin.pageId,
            sourceData: p.plugin.sourceData,
            mode: p.mode,
          })
        } else if (p.mode === 'create') {
          host.load({
            pluginId: '',
            deploymentId: '',
            mode: 'create',
            suggestedName: p.suggestedName,
          })
        }
      } else {
        console.log('[BuilderIframe] ready before session token — deferring load')
        if (pendingLoadTimerRef.current != null) window.clearTimeout(pendingLoadTimerRef.current)
        pendingLoadTimerRef.current = window.setTimeout(() => {
          pendingLoadTimerRef.current = null
          // Token showed up in time → the late-token effect already loaded.
          if (sentTokenRef.current || !hostRef.current || connectionStateRef.current !== 'ready') return
          const q = propsRef.current
          console.warn('[BuilderIframe] session token still missing — loading without it (legacy behavior)')
          lastLoadedIdentityRef.current = identityOf(q.plugin, q.mode)
          if (q.plugin) {
            hostRef.current.load({
              pluginId: q.plugin.pluginId,
              deploymentId: q.plugin.deploymentId,
              pageId: q.plugin.pageId,
              sourceData: q.plugin.sourceData,
              mode: q.mode,
            })
          } else if (q.mode === 'create') {
            hostRef.current.load({ pluginId: '', deploymentId: '', mode: 'create', suggestedName: q.suggestedName })
          }
        }, 4000)
      }

      if (p.funnelContext && p.funnelId) {
        host.sendFunnelContext({
          funnelId: p.funnelId,
          funnelName: p.funnelContext.funnelName,
          steps: p.funnelContext.steps.map(s => ({
            ...s,
            isCurrentEdit: s.id === p.stepId,
          })),
          edges: p.funnelContext.edges,
          currentStepId: p.stepId,
        })
      }
    })

    // ─── Handle: Dirty State ───────────────────────────────────────
    host.on('dirty', ({ isDirty }) => {
      onDirtyChangeRef.current?.(isDirty)
    })

    // ─── Handle: Save ──────────────────────────────────────────────
    host.on('save', async (payload) => {
      const { _requestId } = payload as typeof payload & { _requestId?: string }
      console.log('[BuilderIframe] Builder deployed and saved:', { 
        pluginId: payload.pluginId,
        deploymentId: payload.deploymentId,
        instanceId: payload.instanceId,
      })

      setIsSaving(true)
      try {
        await onSaveRef.current?.(payload)
        host.saveAck({ success: true, _requestId })
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to update step'
        host.saveAck({ success: false, error: errorMsg, _requestId })
        toast.error(errorMsg)
      } finally {
        setIsSaving(false)
      }
    })

    // ─── Handle: Preview Request ───────────────────────────────────
    host.on('request-preview', async (payload) => {
      const { _requestId, ...requestPayload } = payload as typeof payload & { _requestId?: string }
      const handler = onRequestPreviewRef.current
      if (!handler) {
        console.warn('[BuilderIframe] No onRequestPreview handler — cannot create preview session')
        host.sendPreviewSession({ token: '', funnelSessionId: '', previewUrl: '', error: 'Preview not available — no handler', _requestId })
        return
      }

      try {
        const session = await handler(requestPayload)
        host.sendPreviewSession({ ...session, _requestId })
      } catch (err) {
        console.error('[BuilderIframe] Preview session creation failed:', err)
        const message = err instanceof Error ? err.message : 'Failed to create preview session'
        toast.error(message)
        host.sendPreviewSession({ token: '', funnelSessionId: '', previewUrl: '', error: message, _requestId })
      }
    })

    // ─── Handle: Resources Request ─────────────────────────────────
    host.on('request-resources', async (payload) => {
      const { _requestId, ...requestPayload } = payload as typeof payload & { _requestId?: string }
      const handler = onRequestResourcesRef.current
      const empty = { products: [], offers: [], shippingRates: [], orders: [] }
      if (!handler) {
        host.sendResources({ ...empty, _requestId })
        return
      }
      try {
        const resources = await handler(requestPayload)
        host.sendResources({ ...resources, _requestId })
      } catch (err) {
        console.error('[BuilderIframe] Resources fetch failed:', err)
        host.sendResources({ ...empty, _requestId })
      }
    })

    // ─── Handle: Media Library Request ──────────────────────────────
    host.on('request-media-library', async (payload) => {
      const { _requestId, ...requestPayload } = payload as typeof payload & { _requestId?: string }
      const handler = onRequestMediaLibraryRef.current
      if (!handler) {
        host.sendMediaLibrary({ assets: [], pagination: { hasNext: false, total: 0, page: 0 }, _requestId })
        return
      }
      try {
        const result = await handler(requestPayload)
        host.sendMediaLibrary({ ...result, _requestId })
      } catch (err) {
        console.error('[BuilderIframe] Media library fetch failed:', err)
        host.sendMediaLibrary({ assets: [], pagination: { hasNext: false, total: 0, page: 0 }, _requestId })
      }
    })

    // ─── Handle: Register Media Asset ─────────────────────────────
    host.on('register-media-asset', async (payload) => {
      const { _requestId, ...requestPayload } = payload as typeof payload & { _requestId?: string }
      const handler = onRegisterMediaAssetRef.current
      if (!handler) {
        console.warn('[BuilderIframe] No onRegisterMediaAsset handler')
        host.sendMediaAssetRegistered({ id: '', url: '', _requestId, error: 'No handler registered' } as any)
        return
      }
      try {
        const result = await handler(requestPayload)
        host.sendMediaAssetRegistered({ ...result, _requestId })
      } catch (err) {
        console.error('[BuilderIframe] Media asset registration failed:', err)
        const message = err instanceof Error ? err.message : 'Registration failed'
        host.sendMediaAssetRegistered({ id: '', url: '', _requestId, error: message } as any)
      }
    })

    // ─── Handle: Close ─────────────────────────────────────────────
    host.on('close', () => {
      console.log('[BuilderIframe] Builder requested close')
      onCloseRef.current?.()
    })

    // ─── Handle: Navigate Step ──────────────────────────────────────
    host.on('navigate-step', (payload) => {
      console.log('[BuilderIframe] Builder requested step navigation:', payload)
      onNavigateStepRef.current?.(payload.stepId)
    })

    // ─── Handle: Resize ────────────────────────────────────────────
    host.on('resize', (payload) => {
      if (payload.fullscreen !== undefined) {
        setIsFullscreen(payload.fullscreen)
      }
    })

    // Connect to iframe when it loads
    const iframe = iframeRef.current
    if (iframe) {
      host.connect(iframe)
    }

    // Timeout for connection (use ref to avoid stale closure)
    const timeout = setTimeout(() => {
      if (connectionStateRef.current === 'connecting') {
        console.warn('[BuilderIframe] Connection timeout — builder may not support the protocol')
        updateConnectionState('error')
      }
    }, 15000)

    return () => {
      clearTimeout(timeout)
      if (pendingLoadTimerRef.current != null) {
        window.clearTimeout(pendingLoadTimerRef.current)
        pendingLoadTimerRef.current = null
      }
      host.disconnect()
      hostRef.current = null
    }
    // NOT keyed on pluginIdentity: design switches no longer remount the
    // iframe (audit FE-05) — the running Studio receives a load() message
    // instead (softNavigate or the in-place effect below). The host lives as
    // long as the iframe element does.
  }, [builderUrl, storeId])

  // In-place design switch: when the plugin identity prop changes while
  // Studio is already running (e.g. handleOpenVisualEditor fallback picking
  // a different design), send init+load instead of remounting the whole
  // iframe — a remount was a full Studio cold boot on EVERY step navigation
  // (audit FE-05). Skips when softNavigate already delivered this identity,
  // and while connecting (the ready handler loads the latest propsRef).
  useEffect(() => {
    if (connectionStateRef.current !== 'ready' || !hostRef.current) return
    if (lastLoadedIdentityRef.current === pluginIdentity) return
    lastLoadedIdentityRef.current = pluginIdentity

    const p = propsRef.current
    console.log('[BuilderIframe] Design changed in place, sending init + load:', pluginIdentity)
    hostRef.current.init({
      storeId: p.storeId,
      funnelId: p.funnelId,
      stepId: p.stepId,
      stepType: p.stepType,
      sessionToken: p.sessionToken,
      capabilities: ['preview', 'resources'],
      stepConfig: p.stepConfig,
      ui: { theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light' },
    })
    if (p.plugin) {
      hostRef.current.load({
        pluginId: p.plugin.pluginId,
        deploymentId: p.plugin.deploymentId,
        pageId: p.plugin.pageId,
        sourceData: p.plugin.sourceData,
        mode: p.mode,
      })
    } else if (p.mode === 'create') {
      hostRef.current.load({ pluginId: '', deploymentId: '', mode: 'create', suggestedName: p.suggestedName })
    }
  }, [pluginIdentity])

  // Send init + load when sessionToken arrives late (after the ready
  // handshake already fired). With FE-12 this is the NORMAL cold-open path:
  // ready deferred the load, so this is the first (and only) load.
  useEffect(() => {
    if (!sessionToken || connectionStateRef.current !== 'ready' || !hostRef.current) return
    if (sentTokenRef.current === sessionToken) return
    sentTokenRef.current = sessionToken
    if (pendingLoadTimerRef.current != null) {
      window.clearTimeout(pendingLoadTimerRef.current)
      pendingLoadTimerRef.current = null
    }

    const p = propsRef.current
    lastLoadedIdentityRef.current = identityOf(p.plugin, p.mode)
    console.log('[BuilderIframe] Session token arrived, sending init + load')
    hostRef.current.init({
      storeId: p.storeId,
      funnelId: p.funnelId,
      stepId: p.stepId,
      stepType: p.stepType,
      sessionToken,
      capabilities: ['preview', 'resources'],
      stepConfig: p.stepConfig,
      ui: {
        theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
      },
    })

    if (p.plugin) {
      hostRef.current.load({
        pluginId: p.plugin.pluginId,
        deploymentId: p.plugin.deploymentId,
        pageId: p.plugin.pageId,
        sourceData: p.plugin.sourceData,
        mode: p.mode,
      })
    } else if (p.mode === 'create') {
      hostRef.current.load({
        pluginId: '',
        deploymentId: '',
        mode: 'create',
        suggestedName: p.suggestedName,
      })
    }
  }, [sessionToken])

  // Push stepConfig changes to the builder in real time
  const prevStepConfigRef = useRef<string | null>(null)
  useEffect(() => {
    if (connectionStateRef.current !== 'ready' || !hostRef.current) return
    const serialized = stepConfig ? JSON.stringify(stepConfig) : null
    if (serialized === prevStepConfigRef.current) return
    prevStepConfigRef.current = serialized
    hostRef.current.sendStepConfig(stepConfig ?? null)
  }, [stepConfig])

  // Push funnelContext changes to the builder in real time
  const prevFunnelContextRef = useRef<string | null>(null)
  useEffect(() => {
    if (connectionStateRef.current !== 'ready' || !hostRef.current || !funnelContext || !funnelId) return
    const serialized = JSON.stringify(funnelContext)
    if (serialized === prevFunnelContextRef.current) return
    prevFunnelContextRef.current = serialized
    hostRef.current.sendFunnelContext({
      funnelId,
      funnelName: funnelContext.funnelName,
      steps: funnelContext.steps.map(s => ({
        ...s, isCurrentEdit: s.id === stepId,
      })),
      edges: funnelContext.edges,
      currentStepId: stepId,
    })
  }, [funnelContext, funnelId, stepId])

  /** Request save from the builder (called by parent, e.g. CRM save button) */
  const requestSave = useCallback(() => {
    hostRef.current?.requestSave()
  }, [])

  /** Soft-navigate: send init + load to the running Studio without remounting */
  const softNavigate = useCallback((opts: {
    stepId: string; stepType?: StepType; sessionToken: string
    plugin: { pluginId: string; deploymentId: string; pageId?: string; sourceData?: unknown } | null
    mode?: 'edit' | 'create'; stepConfig?: Record<string, unknown> | null
  }) => {
    const host = hostRef.current
    if (!host || connectionStateRef.current !== 'ready') {
      // Do NOT re-enter onNavigateStep here — that's the caller, and looping
      // through it created one builder session per retry until Studio booted
      // (audit FE-05). The parent has already updated the design/token props,
      // so the ready handler will init+load the latest propsRef when the
      // (still booting) Studio connects.
      console.warn('[BuilderIframe] softNavigate before ready — deferring to the ready handshake')
      return
    }
    console.log('[BuilderIframe] Soft navigating to step:', opts.stepId)
    lastLoadedIdentityRef.current = identityOf(opts.plugin, opts.mode)

    const p = propsRef.current
    host.init({
      storeId: p.storeId,
      funnelId: p.funnelId,
      stepId: opts.stepId,
      stepType: opts.stepType,
      sessionToken: opts.sessionToken,
      capabilities: ['preview', 'resources'],
      stepConfig: opts.stepConfig,
      ui: { theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light' },
    })

    if (opts.plugin) {
      host.load({
        pluginId: opts.plugin.pluginId,
        deploymentId: opts.plugin.deploymentId,
        pageId: opts.plugin.pageId,
        sourceData: opts.plugin.sourceData,
        mode: opts.mode || 'edit',
      })
    } else if (opts.mode === 'create') {
      host.load({ pluginId: '', deploymentId: '', mode: 'create' })
    }

    if (p.funnelContext && p.funnelId) {
      host.sendFunnelContext({
        funnelId: p.funnelId,
        funnelName: p.funnelContext.funnelName,
        steps: p.funnelContext.steps.map(s => ({ ...s, isCurrentEdit: s.id === opts.stepId })),
        edges: p.funnelContext.edges,
        currentStepId: opts.stepId,
      })
    }
  }, [])

  useImperativeHandle(ref, () => ({ requestSave, softNavigate }), [requestSave, softNavigate])

  // Build the iframe URL — no stepId to avoid remounts; step changes use softNavigate
  const iframeSrc = (() => {
    const url = new URL(builderUrl)
    url.searchParams.set('embedded', 'true')
    return url.toString()
  })()

  const fullscreenClasses = isFullscreen 
    ? 'fixed inset-0 z-50 bg-white dark:bg-slate-900' 
    : ''

  // Unified loading state: authenticating → connecting → ready
  const isAuthenticating = !sessionToken && !authError
  const isLoading = isAuthenticating || connectionState === 'connecting'

  return (
    <div className={`relative flex flex-col ${fullscreenClasses} ${className}`}>
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white dark:bg-slate-950">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-primary dark:border-slate-700 dark:border-t-primary" style={{ animationDuration: '0.7s' }} />
        </div>
      )}

      {/* Auth error state */}
      {authError && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white dark:bg-slate-950">
          <div className="text-center max-w-sm">
            <AlertCircle className="h-8 w-8 mx-auto text-red-500 mb-2" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Authentication failed
            </p>
            <p className="text-xs text-slate-500 mb-3">
              Could not authenticate with Studio.
            </p>
            {onRetryAuth && (
              <Button size="sm" variant="outline" onClick={onRetryAuth}>
                Retry
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Connection error state */}
      {connectionState === 'error' && !authError && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white dark:bg-slate-950">
          <div className="text-center max-w-sm">
            <AlertCircle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Editor connection failed
            </p>
            <p className="text-xs text-slate-500 mb-3">
              The visual editor could not be loaded. Check that Studio is running.
            </p>
            <Button size="sm" variant="outline" onClick={onClose}>
              Back to Funnel
            </Button>
          </div>
        </div>
      )}

      {/* Saving overlay */}
      {isSaving && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm">
          <div className="text-center">
            <Loader2 className="h-6 w-6 mx-auto animate-spin text-primary mb-2" />
            <p className="text-sm text-slate-600">Saving design...</p>
          </div>
        </div>
      )}

      {/* The iframe mounts ONCE per editor session — design/step switches are
          delivered to the running Studio via load() messages (softNavigate or
          the pluginIdentity effect), never by remounting (audit FE-05: the old
          key={pluginIdentity} made every step navigation a full Studio cold
          boot). It must always mount so the ready/init handshake can run;
          Studio boots and receives the session token via init() postMessage.
          The unauthenticated cold-start window is handled in Studio (Layer A:
          callTrpc skips when there is no auth header) rather than by delaying
          the iframe.
          local-network-access: Chrome 142+ LNA (WebSockets gated since 147)
          blocks the cross-origin Studio iframe's MCP agent bridge to
          ws://localhost:14333 unless the host delegates the permission
          (TAG-2094). */}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        className="flex-1 w-full border-0"
        style={{ minHeight: isFullscreen ? '100vh' : '500px' }}
        allow="clipboard-read; clipboard-write; local-network-access"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        title="Visual Editor"
      />
    </div>
  )
})

/**
 * Hook for parent components to get a ref to control the BuilderIframe.
 */
export function useBuilderIframeRef() {
  return useRef<BuilderIframeHandle | null>(null)
}
